/**
 * useChartSelection — Shared hook for chart renderer selection state.
 *
 * Created s67: extracted from BarChart/LineChart/PieChart renderers
 * to eliminate ~60 lines of duplicated selection + validation lifecycle.
 *
 * Handles:
 * - Selected index state with toggle
 * - Reset on question change
 * - Validation rising-edge detection
 * - Ready state notification
 * - Keyboard navigation (ArrowLeft/Right, Enter/Space)
 */

import { useEffect, useState, useRef, useCallback } from 'react'

interface UseChartSelectionProps {
  questionId: string
  itemCount: number
  idPrefix: string // e.g. 'bar-option', 'line-option', 'pie-option'
  onAnswer: (answer: string | number) => void
  onReadyChange?: (ready: boolean) => void
  shouldValidate?: boolean
}

export function useChartSelection({
  questionId,
  itemCount,
  idPrefix,
  onAnswer,
  onReadyChange,
  shouldValidate,
}: UseChartSelectionProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const prevValidateRef = useRef(false)

  // Reset on question change
  useEffect(() => {
    setSelectedIndex(null)
    prevValidateRef.current = false
  }, [questionId])

  // Reset validation flag
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false
    }
  }, [shouldValidate])

  // Notify readiness
  useEffect(() => {
    onReadyChange?.(selectedIndex !== null)
  }, [selectedIndex, onReadyChange])

  // Rising-edge validation
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current && selectedIndex !== null) {
      prevValidateRef.current = true
      onAnswer(String(selectedIndex))
    }
  }, [shouldValidate, selectedIndex, onAnswer])

  const handleItemClick = useCallback((_data: unknown, index: number) => {
    setSelectedIndex((prev) => (prev === index ? null : index))
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleItemClick(null, index)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        const next = (index + 1) % itemCount
        document.getElementById(`${idPrefix}-${next}`)?.focus()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        const prev = (index - 1 + itemCount) % itemCount
        document.getElementById(`${idPrefix}-${prev}`)?.focus()
      }
    },
    [itemCount, idPrefix, handleItemClick],
  )

  return { selectedIndex, handleItemClick, handleKeyDown }
}
